import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Field, FieldArray, Form, Formik } from "formik";
import { toast } from "react-toastify";
import * as Yup from "yup";

import AppModal from "../AppModal";
import AuthField from "../Auth/AuthField";
import { AuthContext } from "../../context/Auth/AuthContext";
import { WhatsAppsContext } from "../../context/WhatsApp/WhatsAppsContext";
import toastError from "../../errors/toastError";
import api from "../../services/api";
import { i18n } from "../../translate/i18n";

const CHANNEL_ICONS = {
	whatsapp: "https://img.icons8.com/color/48/000000/whatsapp.png",
	telegram: "https://img.icons8.com/color/48/000000/telegram-app--v1.png",
	whatsappcloud:
		"https://e7.pngegg.com/pngimages/362/594/png-clipart-whatsapp-inc-business-whatsapp-text-logo.png",
	whatsapp_business_account:
		"https://e7.pngegg.com/pngimages/362/594/png-clipart-whatsapp-inc-business-whatsapp-text-logo.png",
};

const sanitizePhoneNumber = (value) => String(value || "").replace(/\D/g, "");

const DEFAULT_DDI = "55";
const MAX_E164_DIGITS = 15;

// Só números BR (55 + DDD + 8/9 dígitos) podem ser separados com segurança; os demais ficam inteiros no campo número.
const splitPhone = (full) => {
	const digits = sanitizePhoneNumber(full);
	if (!digits) return { ddi: DEFAULT_DDI, number: "" };
	if (digits.startsWith(DEFAULT_DDI) && (digits.length === 12 || digits.length === 13)) {
		return { ddi: DEFAULT_DDI, number: digits.slice(2) };
	}
	return { ddi: "", number: digits };
};

const isUnchangedLegacyNumber = (parent) =>
	!parent.ddi && !!parent.originalNumber && parent.number === parent.originalNumber;

// Em Telegram e outros canais o "number" é o ID da plataforma, sem DDI.
const WHATSAPP_CHANNELS = new Set([
	"whatsapp",
	"whatsappbusiness",
	"whatsappcloud",
	"whatsapp_business_account",
]);
const isPhoneChannel = (channel) => WHATSAPP_CHANNELS.has(String(channel || "").toLowerCase());

const phoneFieldsFor = (channel, fullNumber) => {
	const digits = sanitizePhoneNumber(fullNumber);
	if (!isPhoneChannel(channel)) return { ddi: "", number: digits, originalNumber: "" };
	const phone = splitPhone(digits);
	return { ...phone, originalNumber: phone.ddi ? "" : phone.number };
};

const buildContactSchema = (resolveChannel) => Yup.object().shape({
	name: Yup.string()
		.min(2, "Too Short!")
		.max(50, "Too Long!")
		.required("Required"),
	ddi: Yup.string().test("ddi", function validateDdi(value) {
		if (!isPhoneChannel(resolveChannel(this.parent.channel))) return true;
		if (!this.parent.number || isUnchangedLegacyNumber(this.parent)) return true;
		if (!/^[1-9]\d{0,2}$/.test(value || "")) {
			return this.createError({ message: i18n.t("contactModal.form.ddiRequired") });
		}
		return true;
	}),
	number: Yup.string().test("number", function validateNumber(value) {
		const number = sanitizePhoneNumber(value);
		if (!isPhoneChannel(resolveChannel(this.parent.channel))) return true;
		if (!number || isUnchangedLegacyNumber(this.parent)) return true;
		const ddi = sanitizePhoneNumber(this.parent.ddi);
		if (ddi === DEFAULT_DDI) {
			const validBr = /^[1-9]{2}\d{8}$/.test(number) || /^[1-9]{2}9\d{8}$/.test(number);
			return validBr || this.createError({ message: i18n.t("contactModal.form.numberBrInvalid") });
		}
		const validIntl = number.length >= 4 && ddi.length + number.length <= MAX_E164_DIGITS;
		return validIntl || this.createError({ message: i18n.t("contactModal.form.numberInvalid") });
	}),
	email: Yup.string().email("Invalid email"),
});

const initialState = {
	name: "",
	ddi: DEFAULT_DDI,
	number: "",
	originalNumber: "",
	email: "",
	company: "",
	channel: "",
	extraInfo: [],
};

function IconTrash() {
	return (
		<svg className="size-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
			<polyline points="3 6 5 6 21 6" />
			<path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
		</svg>
	);
}

function ContactModal({ open, onClose, contactId, initialValues, onSave }) {
	const isMounted = useRef(true);
	const { whatsApps } = useContext(WhatsAppsContext);
	const { user } = useContext(AuthContext);
	const [contact, setContact] = useState(initialState);
	const [loading, setLoading] = useState(false);

	const filteredWhatsApps = useMemo(
		() =>
			(whatsApps || [])
				.filter(
					(whatsApp) =>
						whatsApp.channel !== "facebook" && whatsApp.channel !== "instagram"
				),
		[whatsApps, user.profile, user?.whatsapp?.id]
	);

	const resolveChannel = useCallback(
		(connectionId) =>
			filteredWhatsApps.find((w) => String(w.id) === String(connectionId))?.channel || "",
		[filteredWhatsApps]
	);

	const contactSchema = useMemo(() => buildContactSchema(resolveChannel), [resolveChannel]);

	const handleClose = () => {
		onClose();
		setContact(initialState);
	};

	const handleSaveContact = async (values) => {
		try {
			const selectedChannel =
				filteredWhatsApps.find((wpp) => String(wpp.id) === String(values.channel))
					?.channel || null;

			const channel =
				selectedChannel === "whatsappcloud" ? "whatsappbusiness" : selectedChannel;

			const localNumber = sanitizePhoneNumber(values.number);
			const fullNumber =
				localNumber && isPhoneChannel(selectedChannel)
					? sanitizePhoneNumber(values.ddi) + localNumber
					: localNumber;

			const dados = {
				channel,
				company: values.company,
				email: values.email,
				name: values.name,
				number: fullNumber,
				extraInfo: values.extraInfo,
			};

			if (contactId) {
				await api.put(`/contacts/${contactId}`, dados);
				handleClose();
			} else {
				const { data } = await api.post("/contacts", dados);
				if (onSave) {
					onSave(data);
				}
				handleClose();
			}
			toast.success(i18n.t("contactModal.success"));
		} catch (err) {
			toastError(err);
		}
	};

	useEffect(
		() => () => {
			isMounted.current = false;
		},
		[]
	);

	const hasInitializedRef = useRef(false);
	const initialValuesRef = useRef(null);

	useEffect(() => {
		if (!open) {
			hasInitializedRef.current = false;
			initialValuesRef.current = null;
			return;
		}
		if (hasInitializedRef.current) return;
		hasInitializedRef.current = true;
		initialValuesRef.current = initialValues || null;

		const fetchContact = async () => {
			if (!contactId) {
				const parsed = initialValuesRef.current || {};
				const defaultChannel = filteredWhatsApps[0]?.id || "";
				const connectionId = parsed.channel || defaultChannel;
				setContact({
					...initialState,
					...parsed,
					name: parsed.name || "",
					...phoneFieldsFor(resolveChannel(connectionId), parsed.number),
					channel: connectionId,
					extraInfo: parsed.extraInfo || [],
				});
				return;
			}

			setLoading(true);
			try {
				const { data } = await api.get(`/contacts/${contactId}`);
				if (isMounted.current) {
					const matchedWpp = filteredWhatsApps.find(
						(w) =>
							w.channel === data.channel ||
							(w.channel === "whatsappcloud" && data.channel === "whatsappbusiness")
					);
					setContact({
						...data,
						...phoneFieldsFor(matchedWpp?.channel ?? data.channel, data.number),
						channel: matchedWpp?.id ?? data.channel ?? "",
						extraInfo: data.extraInfo || [],
					});
				}
			} catch (err) {
				toastError(err);
			} finally {
				if (isMounted.current) setLoading(false);
			}
		};

		fetchContact();
	}, [contactId, open, filteredWhatsApps, resolveChannel]);

	const title = contactId
		? i18n.t("contactModal.title.edit")
		: i18n.t("contactModal.title.add");

	return (
		<Formik
			initialValues={contact}
			enableReinitialize
			validationSchema={contactSchema}
			onSubmit={(values, actions) => {
				handleSaveContact(values);
				actions.setSubmitting(false);
			}}
		>
			{({ values, errors, touched, isSubmitting, handleChange, handleBlur, setFieldValue }) => {
				const numberError =
					(touched.ddi && errors.ddi) || (touched.number && errors.number);
				const phoneChannel = isPhoneChannel(resolveChannel(values.channel));

				const handleChannelChange = (event) => {
					const nextId = event.target.value;
					const wasPhone = phoneChannel;
					const willBePhone = isPhoneChannel(resolveChannel(nextId));
					setFieldValue("channel", nextId);
					if (wasPhone === willBePhone) return;
					const full = wasPhone
						? sanitizePhoneNumber(values.ddi) + sanitizePhoneNumber(values.number)
						: values.number;
					const next = phoneFieldsFor(resolveChannel(nextId), full);
					setFieldValue("ddi", next.ddi);
					setFieldValue("number", next.number);
					setFieldValue("originalNumber", next.originalNumber);
				};

				return (
					<AppModal
						open={open}
						onClose={handleClose}
						title={title}
						maxWidth={640}
						footer={
							<>
								<button
									type="button"
									className="btn-secondary"
									onClick={handleClose}
									disabled={isSubmitting}
								>
									{i18n.t("contactModal.buttons.cancel")}
								</button>
								<button
									type="submit"
									form="contact-form"
									className="btn-primary"
									disabled={isSubmitting || loading}
								>
									{isSubmitting
										? "..."
										: contactId
											? i18n.t("contactModal.buttons.okEdit")
											: i18n.t("contactModal.buttons.okAdd")}
								</button>
							</>
						}
					>
						{loading ? (
							<div className="app-modal-loading">
								<div className="app-modal-spinner" />
							</div>
						) : (
							<Form id="contact-form" className="space-y-1">
								<p className="mb-3 text-sm font-semibold text-default-800 dark:text-default-100">
									{i18n.t("contactModal.form.connection")}
								</p>
								<AuthField id="contact-channel">
									<select
										id="contact-channel"
										name="channel"
										className="form-select w-full"
										aria-label={i18n.t("contactModal.form.connection")}
										value={values.channel}
										onChange={handleChannelChange}
										onBlur={handleBlur}
									>
										{filteredWhatsApps.map((whatsApp) => (
											<option key={whatsApp.id} value={whatsApp.id}>
												{whatsApp.name}
											</option>
										))}
									</select>
								</AuthField>
								<div className="mb-2 flex flex-wrap gap-2">
									{filteredWhatsApps
										.filter((w) => String(w.id) === String(values.channel))
										.map((w) => {
											const icon =
												CHANNEL_ICONS[w.channel] || CHANNEL_ICONS.whatsappcloud;
											return icon ? (
												<span
													key={w.id}
													className="inline-flex items-center gap-2 rounded border border-default-200 bg-default-100 px-2 py-1 text-xs text-default-600 dark:border-default-600 dark:bg-default-800 dark:text-default-300"
												>
													<img src={icon} alt="" className="size-5 object-contain" />
													{w.name}
												</span>
											) : null;
										})}
								</div>

								<p className="mb-3 mt-4 text-sm font-semibold text-default-800 dark:text-default-100">
									{i18n.t("contactModal.form.mainInfo")}
								</p>
								<AuthField
									id="contact-name"
									label={i18n.t("contactModal.form.name")}
									error={touched.name && errors.name}
								>
									<input
										id="contact-name"
										name="name"
										type="text"
										className="form-input w-full"
										value={values.name}
										onChange={handleChange}
										onBlur={handleBlur}
										autoFocus
									/>
								</AuthField>
								{(user.userSeeNumbers || user.profile === "admin" || contactId === null) ? (
									<AuthField
										id="contact-number"
										label={i18n.t("contactModal.form.number")}
										error={numberError}
										hint={phoneChannel ? i18n.t("contactModal.form.numberHint") : undefined}
									>
										<div className="flex gap-2">
											{phoneChannel && (
											<div className="relative w-24 shrink-0">
												<span className="pointer-events-none absolute inset-y-0 start-3 flex items-center text-sm text-default-500">
													+
												</span>
												<input
													id="contact-ddi"
													name="ddi"
													type="tel"
													inputMode="numeric"
													maxLength={3}
													className="form-input w-full ps-6"
													placeholder={DEFAULT_DDI}
													aria-label={i18n.t("contactModal.form.ddi")}
													value={values.ddi}
													onChange={(event) => {
														setFieldValue("ddi", sanitizePhoneNumber(event.target.value).slice(0, 3));
													}}
													onBlur={handleBlur}
												/>
											</div>
											)}
											<input
												id="contact-number"
												name="number"
												type="tel"
												inputMode="numeric"
												className="form-input min-w-0 flex-1"
												placeholder={phoneChannel && values.ddi === DEFAULT_DDI ? "13912344321" : ""}
												value={values.number}
												onChange={(event) => {
													setFieldValue("number", sanitizePhoneNumber(event.target.value));
												}}
												onBlur={handleBlur}
											/>
										</div>
									</AuthField>) : (
									<AuthField
										id="contact-number"
										label={i18n.t("contactModal.form.number")}
										hint="Você não tem permissão para visualizar este campo."
									>
										<input
											className="form-input w-full"
											value="************"
											disabled
											readOnly
										/>
									</AuthField>
								)}
								<AuthField
									id="contact-email"
									label={i18n.t("contactModal.form.email")}
									error={touched.email && errors.email}
								>
									<input
										id="contact-email"
										name="email"
										type="email"
										className="form-input w-full"
										placeholder="email@exemplo.com"
										value={values.email}
										onChange={handleChange}
										onBlur={handleBlur}
									/>
								</AuthField>
								<AuthField id="contact-company" label={i18n.t("contactModal.form.company")}>
									<input
										id="contact-company"
										name="company"
										type="text"
										className="form-input w-full"
										value={values.company}
										onChange={handleChange}
										onBlur={handleBlur}
									/>
								</AuthField>

								<p className="mb-3 mt-4 text-sm font-semibold text-default-800 dark:text-default-100">
									{i18n.t("contactModal.form.extraInfo")}
								</p>
								<FieldArray name="extraInfo">
									{({ push, remove }) => (
										<div className="space-y-3">
											{values.extraInfo?.map((info, index) => (
												<div
													key={`extra-${index}`}
													className="flex items-start gap-2"
												>
													<div className="grid min-w-0 flex-1 gap-3 sm:grid-cols-2">
														<AuthField
															id={`extra-name-${index}`}
															label={i18n.t("contactModal.form.extraName")}
														>
															<Field
																as="input"
																name={`extraInfo[${index}].name`}
																className="form-input w-full"
															/>
														</AuthField>
														<AuthField
															id={`extra-value-${index}`}
															label={i18n.t("contactModal.form.extraValue")}
														>
															<Field
																as="input"
																name={`extraInfo[${index}].value`}
																className="form-input w-full"
															/>
														</AuthField>
													</div>
													<button
														type="button"
														className="btn btn-icon mt-7 size-8 shrink-0 border border-default-200 bg-transparent text-danger hover:bg-danger/10 dark:border-default-600"
														onClick={() => remove(index)}
														aria-label={i18n.t("contacts.buttons.delete")}
													>
														<IconTrash />
													</button>
												</div>
											))}
											<button
												type="button"
												className="btn btn-sm w-full border border-dashed border-primary bg-transparent text-primary hover:bg-primary/10"
												onClick={() => push({ name: "", value: "" })}
											>
												+ {i18n.t("contactModal.buttons.addExtraInfo")}
											</button>
										</div>
									)}
								</FieldArray>
							</Form>
						)}
					</AppModal>
				);
			}}
		</Formik>
	);
}

export default ContactModal;
